/**
 * Wire → UI adapter for the review workspace.
 *
 * All backend format quirks live here: `warn`↔`warning`, 0-based page
 * indices → 1-based for the surface, `bbox_json` string parsing, KPI/citation
 * CSV splitting. Every consumer downstream sees a `Review` shape only —
 * wire types never escape this file.
 */

import type {
  Authority,
  Evidence,
  Finding,
  FindingAxes,
  FindingGroup,
  FindingId,
  FindingPlacement,
  FindingSeverity,
  PointRect,
  Review,
  ReviewId,
} from "../data/review-data"
import type {
  WireFinding,
  WireFindingOccurrence,
  WireReview,
  WireSeverity,
} from "./wire"

/** Backend `warn` maps to our `warning`; other values pass through. */
export function severityFromWire(sev: WireSeverity): FindingSeverity {
  return sev === "warn" ? "warning" : sev
}

export function severityToWire(sev: FindingSeverity): WireSeverity {
  return sev === "warning" ? "warn" : sev
}

/** Parse `"[x0, y0, x1, y1]"`. Returns null on any parse failure. */
export function parseBboxJson(
  raw: string | null,
  pageZeroBased: number | null,
): PointRect | null {
  if (!raw || pageZeroBased == null) return null
  try {
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr) || arr.length < 4) return null
    const [x0, y0, x1, y1] = arr
    if (
      typeof x0 !== "number" ||
      typeof y0 !== "number" ||
      typeof x1 !== "number" ||
      typeof y1 !== "number"
    ) {
      return null
    }
    return { page: pageZeroBased + 1, x0, y0, x1, y1 }
  } catch {
    return null
  }
}

/** Split `"40 CFR 156.10; LRM Ch 7"` into `[Authority, …]` with best-effort kinds. */
export function authoritiesFromCsv(csv: string | null): Authority[] {
  if (!csv) return []
  return csv
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .map<Authority>((label) => {
      const upper = label.toUpperCase()
      if (upper.startsWith("40 CFR") || upper.includes(" CFR ")) {
        return { kind: "cfr", label }
      }
      if (upper.startsWith("LRM")) return { kind: "lrm", label }
      if (upper.startsWith("PR NOTICE") || upper.startsWith("PRN")) {
        return { kind: "prn", label }
      }
      if (upper.startsWith("7 USC") || upper.includes(" USC ")) {
        return { kind: "statute", label }
      }
      return { kind: "internal", label }
    })
}

/** Front-panel/format rules go to g-format; text-diff rules go to g-text. */
function groupFromRule(ruleId: string): FindingGroup {
  const prefix = ruleId.split("-")[0]?.toUpperCase() ?? ""
  if (prefix === "TXT") {
    return { id: "g-text", label: "Text review", ordinal: 3 }
  }
  return { id: "g-format", label: "Formatting review", ordinal: 4 }
}

/**
 * Derive the three-way placement from the wire finding. `box` = has
 * coordinates; `page` = named a page but no box; `document` = neither.
 * This is the same contract as backend `placementOf` but implemented FE-side
 * since the wire response doesn't include a derived enum today.
 */
function placementFromWire(
  bbox: PointRect | null,
  page: number | null,
): FindingPlacement {
  if (bbox) return "box"
  if (page != null) return "page"
  return "document"
}

function axesFromWire(f: WireFinding): FindingAxes {
  // Backend SME verdict is confirm|reject|modify. `modify` is "SME accepted
  // with edits" — still a confirmation from the finding-validity perspective,
  // so we bucket it as `confirmed` here (the reviewer's edit is stored in
  // sme_note). "needs_sme" as a UI axis comes from expected_system_output,
  // not from sme_decision (which is *set* only after an SME acts).
  const verdict: FindingAxes["verdict"] =
    f.sme_decision === "confirm" || f.sme_decision === "modify"
      ? "confirmed"
      : f.sme_decision === "reject"
        ? "rejected"
        : f.expected_system_output === "needs_sme"
          ? "needs_sme"
          : "unset"

  const clientResolution: FindingAxes["clientResolution"] =
    f.client_resolution === "acknowledged"
      ? "acknowledged"
      : f.client_resolution === "disputed"
        ? "disputed"
        : f.client_resolution === "done"
          ? "done"
          : "none"

  return {
    verdict,
    state: f.state,
    clientResolution,
    origin: f.origin,
  }
}

function bboxFromPrimaryOccurrence(f: WireFinding): PointRect | null {
  // Prefer the FPL occurrence — the printed label is what the reviewer draws
  // on. Fall back to the top-level bbox_json if occurrences is empty (the
  // manual-add path posts these on the finding itself).
  const fplOcc: WireFindingOccurrence | undefined = f.occurrences.find(
    (o) => o.side === "fpl",
  )
  if (fplOcc) return parseBboxJson(fplOcc.bbox_json, fplOcc.page)
  return parseBboxJson(f.bbox_json, f.page)
}

export function fromWireFinding(f: WireFinding, ordinal: number): Finding {
  const bbox = bboxFromPrimaryOccurrence(f)
  const placement = placementFromWire(bbox, f.page != null ? f.page + 1 : null)

  const divergence: Evidence | null =
    f.master_text != null || f.fpl_text != null
      ? { master: f.master_text, printed: f.fpl_text }
      : null

  return {
    id: String(f.id) as FindingId,
    number: ordinal,
    ruleId: f.rule_id,
    severity: severityFromWire(f.severity),
    claim: f.message,
    placement,
    bbox,
    pageHint: f.page != null ? f.page + 1 : null,
    occurrences: Math.max(1, f.occurrences.length),
    axes: axesFromWire(f),
    confidence: f.confidence,
    // Backend doesn't return author name/at on findings today; leave null.
    // Manual findings can carry this once the endpoint is extended.
    authoredBy: null,
    group: groupFromRule(f.rule_id),
    kind: f.origin === "manual" ? "judgment" : "mechanical",
    divergence,
    reasoning: f.reasoning,
    remediation: f.remediation,
    authorities: authoritiesFromCsv(f.citation_csv),
    firstSeenVersion: f.carried_from_finding_id != null ? "carried" : "current",
    // These aren't on the review endpoint — messaging counts come from the
    // comments endpoint. Zero is the safe default until they merge.
    commentCount: 0,
    unreadCount: 0,
    smeNote:
      f.sme_note != null
        ? { by: "SME", at: "", note: f.sme_note }
        : null,
    dismissalNote: null,
  }
}

/**
 * Wire → UI review. Fields the wire doesn't carry today (assignment trail,
 * messages, client attachment) are filled with safe defaults so the surface
 * never crashes on a partial payload; those come from side calls
 * (`useReviewPeopleQuery`, comments query) merged in by the workspace.
 */
export function fromWireReview(w: WireReview): Review {
  const findings = w.findings.map((f, i) => fromWireFinding(f, i + 1))

  return {
    id: String(w.id) as ReviewId,
    epaRegNo: w.reg_nr,
    productName: w.product_name ?? w.reg_nr,
    productKind: "",
    version: `v${w.version_no}`,
    fplName: "fpl.pdf",
    masterName: "master.pdf",
    fplUrl: w.fpl_document_id != null ? `/api/v1/documents/${w.fpl_document_id}/file` : "",
    masterUrl: w.master_document_id != null ? `/api/v1/documents/${w.master_document_id}/file` : "",
    // pageCount is not on the wire (backend PR gap #15 — documents.page_count).
    // pdf.js knows the true page count after it loads the document — the
    // PdfSurface component should update this from the parsed PDFDocumentProxy
    // once available; keep the placeholder at 1 so the paginator does not blow
    // up before the load resolves.
    pageCount: 1,
    createdAt: w.created_at,
    updatedAt: w.completed_at ?? w.created_at,
    assignment: {
      currentStage: w.review_stage,
      archived: w.archived,
      secondarySkipped: w.secondary_skipped,
      assignee: null,
      events: [],
    },
    client: null,
    findings,
    messages: [],
  }
}
